import React from 'react';
import {Table, Tag, Button, Space, Modal} from 'antd';
import type {ColumnsType} from 'antd/es/table';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {ExclamationCircleOutlined} from '@ant-design/icons';

export interface SorterPlanRecord {
    id: string;
    sorterId?: number;
    warehouseId?: number;
    sorterPlanName?: string;
    status: number;
    version: number;
    isDropBag: boolean;
    isSpecified: boolean;
    sorterType?: number;
    isChange: boolean;
    createdAt?: number;
    createdBy?: string;
    updatedAt?: number;
    updatedBy?: string;
}

export interface SorterInfo {
    id: string;
    sorterType: number;
}

interface SorterPlanColumnsProps {
    sorterPlanData: SorterPlanRecord[];
    loading: boolean;
    onDelete: (id: string) => void;
    sorterMap?: Map<string, string>;
    warehouseMap?: Map<string, string>;
}

const SorterPlanColumns: React.FC<SorterPlanColumnsProps> = ({
    sorterPlanData,
    loading,
    onDelete,
    sorterMap,
    warehouseMap,
}) => {
    const {t} = useTranslation();
    const navigate = useNavigate();

    const showDeleteConfirm = (record: SorterPlanRecord) => {
        Modal.confirm({
            title: t('wms.sorterPlan.messages.deleteConfirm'),
            icon: <ExclamationCircleOutlined />,
            content: t('wms.sorterPlan.messages.deleteWarning'),
            okText: t('wms.sorterPlan.messages.deleteConfirmYes'),
            okType: 'danger',
            cancelText: t('wms.sorterPlan.messages.deleteConfirmNo'),
            centered: true,
            onOk() {
                onDelete(record.id);
            },
        });
    };

    const getStatusTag = (status: number, isChange: boolean) => {
        switch (status) {
            case 0:
                return <Tag color="default">{t('wms.sorterPlan.status.unused')}</Tag>;
            case 1:
                return (
                    <Space size={4}>
                        <Tag color="success">{t('wms.sorterPlan.status.inUse')}</Tag>
                        {isChange && (
                            <span style={{ color: '#ff4d4f', fontSize: '12px' }}>
                                {t('wms.sorterPlan.status.hasChanges')}
                            </span>
                        )}
                    </Space>
                );
            default:
                return <Tag color="default">{t('wms.sorterPlan.status.unknown')}</Tag>;
        }
    };

    const columns: ColumnsType<SorterPlanRecord> = [
        {
            title: t('wms.sorterPlan.columns.id'),
            dataIndex: 'id',
            key: 'id',
            width: 80,
        },
        {
            title: t('wms.sorterPlan.columns.sorterPlanName'),
            dataIndex: 'sorterPlanName',
            key: 'sorterPlanName',
            width: 200,
            render: (name: string, record: SorterPlanRecord) => (
                <Button
                    type="link"
                    onClick={() => navigate(`/wms/sorterPlan/${record.id}`, {
                        state: {sorterId: record.sorterId}
                    })}
                    style={{padding: 0}}
                >
                    {name || '--'}
                </Button>
            ),
        },
        {
            title: t('wms.sorterPlan.columns.sorter'),
            dataIndex: 'sorterId',
            key: 'sorterId',
            width: 200,
            render: (sorterId: number) => {
                if (!sorterId) return '-';
                return sorterMap?.get(String(sorterId)) || sorterId;
            },
        },
        {
            title: t('wms.sorterPlan.columns.warehouse'),
            dataIndex: 'warehouseId',
            key: 'warehouseId',
            width: 160,
            render: (warehouseId: number) => {
                if (!warehouseId) return '-';
                return warehouseMap?.get(String(warehouseId)) || warehouseId;
            },
        },
        {
            title: t('wms.sorterPlan.columns.status'),
            dataIndex: 'status',
            key: 'status',
            width: 150,
            render: (status: number, record: SorterPlanRecord) => getStatusTag(status, record.isChange),
        },
        {
            title: t('wms.sorterPlan.columns.version'),
            dataIndex: 'version',
            key: 'version',
            width: 100,
            render: (version: number) => version || 0,
        },
        {
            title: t('wms.sorterPlan.columns.isSpecified'),
            dataIndex: 'isSpecified',
            key: 'isSpecified',
            width: 150,
            render: (isSpecified: boolean) => {
                if (isSpecified) {
                    return <Tag color="blue">{t('wms.sorterPlan.isSpecified.yes')}</Tag>;
                }
                return null;
            },
        },
        {
            title: t('wms.sorterPlan.columns.isDropBag'),
            dataIndex: 'isDropBag',
            key: 'isDropBag',
            width: 120,
            render: (isDropBag: boolean, record: SorterPlanRecord) => {
                // DWS 类型（sorterType = 1）不显示落格组包
                if (record.sorterType === 1) {
                    return null;
                }
                if (isDropBag) {
                    return <Tag color="green">{t('wms.sorterPlan.isDropBag.enabled')}</Tag>;
                }
                return <Tag color="default">{t('wms.sorterPlan.isDropBag.disabled')}</Tag>;
            },
        },
        {
            title: t('wms.sorterPlan.columns.actions'),
            key: 'actions',
            width: 100,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small" className="justify-start">
                    <Button
                        type="link"
                        size="small"
                        danger
                        onClick={() => showDeleteConfirm(record)}
                    >
                        {t('wms.sorterPlan.buttons.delete')}
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <Table
            columns={columns}
            dataSource={sorterPlanData}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: 'max-content' }}
            className="mb-4"
        />
    );
};

export default SorterPlanColumns;
